import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToast } from '@hooks/useApiToast';
import { enterpriseApiClient } from '@api/enterprise/client';
import { WorkOrdersApi, OrderDetailStatus } from '@api/enterprise/openapi';
import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi';
import { workOrderKeys, WORK_ORDERS_QUERY_KEY } from '../constants';
import { db } from '@db/database';
import { enqueue, processAll } from '@services/offlineSyncService';

type IncompleteWorkOrderDetailParams = {
  jobNumber: string;
  sequenceNumber: number;
};

export const useIncompleteWorkOrderDetail = (
  onSuccessCallback?: () => void
) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<
    WorkOrderDetailViewModel,
    Error,
    IncompleteWorkOrderDetailParams
  >({
    mutationFn: async ({
      jobNumber,
      sequenceNumber
    }: IncompleteWorkOrderDetailParams) => {
      if (!navigator.onLine) {
        await db.transaction(
          'rw',
          [db.cachedWorkOrderDetails, db.syncQueue],
          async () => {
            const cached = await db.cachedWorkOrderDetails
              .where('[jobNumber+sequenceNumber]')
              .equals([jobNumber, sequenceNumber])
              .first();

            if (cached) {
              await db.cachedWorkOrderDetails
                .where('[jobNumber+sequenceNumber]')
                .equals([jobNumber, sequenceNumber])
                .modify({
                  status: OrderDetailStatus.Incomplete,
                  completedDate: null
                });
            }

            await enqueue({
              entityType: 'wo_detail_incomplete',
              entityKey: `${jobNumber}-${sequenceNumber}`,
              payload: {
                jobNumber,
                sequenceNumber,
                originalState: cached?.status
              },
              priority: 40
            });
          }
        );

        return {
          jobNumber,
          sequenceNumber,
          status: OrderDetailStatus.Incomplete
        } as WorkOrderDetailViewModel;
      }

      const api = new WorkOrdersApi(undefined, undefined, enterpriseApiClient);
      const response =
        await api.apiWorkOrdersJobNumberDetailsSequenceNumberIncompletePost({
          jobNumber,
          sequenceNumber
        });
      return response.data;
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.byJobNumber(variables.jobNumber)
      });
      queryClient.invalidateQueries({
        queryKey: [WORK_ORDERS_QUERY_KEY, 'details', variables.jobNumber]
      });
      queryClient.invalidateQueries({
        queryKey: ['workOrderDetails', variables.jobNumber]
      });

      showSuccess('detail.incomplete.success', 'workOrders');
      onSuccessCallback?.();

      if (!navigator.onLine) {
        processAll();
      }

      if (navigator.onLine) {
        try {
          const metadata = await db.downloadMetadata.get(
            String(variables.jobNumber)
          );
          if (metadata?.status === 'cached') {
            await db.cachedWorkOrderDetails
              .where('[jobNumber+sequenceNumber]')
              .equals([String(variables.jobNumber), variables.sequenceNumber])
              .modify({
                status: OrderDetailStatus.Incomplete,
                completedDate: null
              });
          }
        } catch {
          // Best effort
        }
      }
    },
    onError: (error, _variables) => {
      showError('detail.incomplete.error', error, 'workOrders');
    }
  });
};
